import React, { useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import InputLabel from "@material-ui/core/InputLabel";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";

import { format, parseISO } from "date-fns";

import { i18n } from "../../translate/i18n";

import ContactModal from "../ContactModal";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import TicketHistoryModal from "../TicketHistoryModal";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import TextField from "@material-ui/core/TextField";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import CheckIcon from "@material-ui/icons/Check";

const drawerWidth = 320;

const useStyles = makeStyles(theme => ({
	drawer: {
		width: "50vw",
		minWidth: 360,
		maxWidth: 650,
		flexShrink: 0,
	},
	drawerPaper: {
		width: "50vw",
		minWidth: 360,
		maxWidth: 650,
		display: "flex",
		borderTop: "1px solid rgba(0, 0, 0, 0.12)",
		borderRight: "1px solid rgba(0, 0, 0, 0.12)",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		borderTopRightRadius: 4,
		borderBottomRightRadius: 4,
	},
	header: {
		display: "flex",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		backgroundColor: "#eee",
		alignItems: "center",
		padding: theme.spacing(0, 1),
		minHeight: "73px",
		justifyContent: "flex-start",
	},
	content: {
		display: "flex",
		backgroundColor: "#eee",
		flexDirection: "column",
		padding: "8px 0px 8px 8px",
		height: "100%",
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},

	contactAvatar: {
		width: 56,
		height: 56,
		marginRight: 12,
	},

	contactHeader: {
		display: "flex",
		padding: "10px 12px",
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "flex-start",
	},

	contactDetails: {
		marginTop: 8,
		padding: 8,
		display: "flex",
		flexDirection: "column",
	},
	contactExtraInfo: {
		marginTop: 4,
		padding: 6,
	},
}));

const DEFAULT_SERVICES_LIST = [
	{ id: "1", name: "🤰 Pré-Natal", color: "#0284c7" },
	{ id: "2", name: "🌸 Ginecologia", color: "#db2777" },
	{ id: "3", name: "🩺 DIU & Implanon", color: "#7c3aed" },
	{ id: "4", name: "🏥 Parto & Cirurgia", color: "#d97706" },
	{ id: "5", name: "🩺 Clínico Geral", color: "#2563eb" },
	{ id: "6", name: "💆‍♀️ Fisioterapia", color: "#059669" }
];

const DEFAULT_STATUSES_LIST = [
	{ id: "1", name: "🟡 Lead Novo (Aguardando CEP / Dados)", color: "#eab308" },
	{ id: "2", name: "🔵 Em Qualificação / Cotação de Valores", color: "#0284c7" },
	{ id: "3", name: "📅 Agendamento Solicitado / Em Negociação", color: "#8b5cf6" },
	{ id: "4", name: "🟢 Agendamento Confirmado", color: "#16a34a" },
	{ id: "5", name: "✅ Atendimento Realizado", color: "#059669" },
	{ id: "6", name: "🔴 Não Agendou / Desistiu", color: "#dc2626" }
];

const ContactDrawer = ({ open, handleDrawerClose, contact, loading }) => {
	const classes = useStyles();
	const history = useHistory();
	const { user } = useContext(AuthContext);

	const [modalOpen, setModalOpen] = useState(false);
	const [historyTicket, setHistoryTicket] = useState(null);
	const [availableServices, setAvailableServices] = useState(DEFAULT_SERVICES_LIST);
	const [availableStatuses, setAvailableStatuses] = useState(DEFAULT_STATUSES_LIST);
	const [availableReminders, setAvailableReminders] = useState([]);

	const [reminderTypeId, setReminderTypeId] = useState("");
	const [reminderDate, setReminderDate] = useState("");
	const [reminderComment, setReminderComment] = useState("");

	const [extraInfoState, setExtraInfoState] = useState(contact?.extraInfo || []);
	const [contactDetails, setContactDetails] = useState(contact);

	React.useEffect(() => {
		setExtraInfoState(contact?.extraInfo || []);
		setContactDetails(contact);
	}, [contact]);

	React.useEffect(() => {
		const fetchFullContact = async () => {
			if (!contact?.id) return;
			try {
				const { data } = await api.get(`/contacts/${contact.id}`);
				setContactDetails(data);
			} catch (err) {
				console.error(err);
			}
		};
		fetchFullContact();
	}, [contact]);

	React.useEffect(() => {
		const fetchSettings = async () => {
			try {
				const { data } = await api.get("/settings");
				const wappId = contact?.whatsappId || 1;
				const servSetting = data.find(s => s.key === `services_list_wapp_${wappId}`) || data.find(s => s.key === "services_list");
				if (servSetting && servSetting.value) {
					setAvailableServices(JSON.parse(servSetting.value));
				}
				const statSetting = data.find(s => s.key === `statuses_list_wapp_${wappId}`) || data.find(s => s.key === "statuses_list");
				if (statSetting && statSetting.value) {
					setAvailableStatuses(JSON.parse(statSetting.value));
				}
				const remSetting = data.find(s => s.key === "reminders_list");
				if (remSetting && remSetting.value) {
					setAvailableReminders(JSON.parse(remSetting.value));
				}
			} catch (err) {
				console.error(err);
			}
		};
		fetchSettings();
	}, [contact]);

	return (
		<Drawer
			className={classes.drawer}
			anchor="right"
			open={open}
			onClose={handleDrawerClose}
			classes={{
				paper: classes.drawerPaper,
			}}
		>
			<div className={classes.header} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingRight: 12 }}>
				<div style={{ display: "flex", alignItems: "center" }}>
					<IconButton onClick={handleDrawerClose}>
						<CloseIcon />
					</IconButton>
					<Typography style={{ fontWeight: "bold", fontSize: "0.95rem" }}>
						👤 Ficha do Cliente
					</Typography>
				</div>
				<span style={{ fontSize: "0.75rem", backgroundColor: "#ecfdf5", color: "#047857", border: "1px solid #a7f3d0", borderRadius: 6, padding: "3px 10px", fontWeight: "bold", whiteSpace: "nowrap" }}>
					📱 {contactDetails?.whatsapp?.name || contact?.whatsapp?.name || "Prime Nutra"}
				</span>
			</div>
			{loading ? (
				<ContactDrawerSkeleton classes={classes} />
			) : (
				<div className={classes.content}>
					<Paper square variant="outlined" className={classes.contactHeader}>
						<Avatar
							alt={contact?.name || ""}
							src={contact?.profilePicUrl}
							className={classes.contactAvatar}
						/>

						<div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Typography variant="subtitle1" style={{ fontWeight: "bold", fontSize: "1rem", lineHeight: 1.2 }}>
									{contact?.name || "Sem Nome"}
								</Typography>
								<Button
									variant="outlined"
									color="primary"
									size="small"
									style={{ textTransform: "none", fontSize: "0.72rem", padding: "2px 8px" }}
									onClick={() => setModalOpen(true)}
								>
									{i18n.t("contactDrawer.buttons.edit")}
								</Button>
							</div>

							<Button
								variant="contained"
								size="small"
								style={{
									backgroundColor: "#25D366",
									color: "#ffffff",
									fontWeight: "bold",
									textTransform: "none",
									fontSize: "0.75rem",
									padding: "3px 8px",
									alignSelf: "flex-start",
								}}
								onClick={async () => {
									if (!contact?.id) return;
									try {
										const { data: ticket } = await api.post("/tickets", {
											contactId: contact.id,
											userId: user?.id,
											status: "open",
											whatsappId: contactDetails?.whatsappId || contact?.whatsappId,
										});
										handleDrawerClose();
										history.push(`/tickets/${ticket.id}`);
									} catch (err) {
										toastError(err);
									}
								}}
							>
								💬 Abrir Chat no WhatsApp ({contact?.number})
							</Button>
						</div>
					</Paper>

					{/* RESUMO HISTÓRICO DE ATENDIMENTOS (TICKETS) */}
					<Paper square variant="outlined" style={{ padding: "10px 12px", marginTop: 8, backgroundColor: "#f8fafc" }}>
						<Typography variant="subtitle2" style={{ fontWeight: "bold", fontSize: "0.82rem", color: "#0f172a", marginBottom: 6 }}>
							🎫 Histórico de Chamados
						</Typography>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
							<div style={{ flex: 1, backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 6, padding: "6px 8px", textAlign: "center" }}>
								<div style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: "bold" }}>TOTAL</div>
								<div style={{ fontSize: "1rem", fontWeight: "bold", color: "#0f172a" }}>
									{contactDetails?.tickets?.length || 0}
								</div>
							</div>
							<div style={{ flex: 1, backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, padding: "6px 8px", textAlign: "center" }}>
								<div style={{ fontSize: "0.68rem", color: "#166534", fontWeight: "bold" }}>ATIVOS</div>
								<div style={{ fontSize: "1rem", fontWeight: "bold", color: "#16a34a" }}>
									{contactDetails?.tickets?.filter(t => t.status === "open" || t.status === "pending")?.length || 0}
								</div>
							</div>
							<div style={{ flex: 1, backgroundColor: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 6, padding: "6px 8px", textAlign: "center" }}>
								<div style={{ fontSize: "0.68rem", color: "#075985", fontWeight: "bold" }}>CONCLUÍDOS</div>
								<div style={{ fontSize: "1rem", fontWeight: "bold", color: "#0284c7" }}>
									{contactDetails?.tickets?.filter(t => t.status === "closed")?.length || 0}
								</div>
							</div>
						</div>

						{/* LISTA DE CHAMADOS POR DATA (CLICÁVEL) */}
						<div style={{ marginTop: 8, maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
							{(!contactDetails?.tickets || contactDetails.tickets.length === 0) && (
								<Typography variant="caption" style={{ color: "#94a3b8" }}>
									Nenhum chamado registrado ainda.
								</Typography>
							)}
							{[...(contactDetails?.tickets || [])]
								.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
								.map(t => {
									const statusMap = {
										open: { text: "🟢 Em Atendimento", bg: "#f0fdf4", color: "#166534" },
										pending: { text: "🟡 Aguardando", bg: "#fffbeb", color: "#b45309" },
										closed: { text: "🔵 Fechado", bg: "#f0f9ff", color: "#075985" },
									};
									const st = statusMap[t.status] || { text: t.status, bg: "#f1f5f9", color: "#334155" };
									return (
										<Paper
											key={t.id}
											variant="outlined"
											style={{
												padding: "6px 8px",
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												cursor: "pointer",
												backgroundColor: "#ffffff",
											}}
											onClick={() => setHistoryTicket(t)}
										>
											<Typography variant="caption" style={{ color: "#334155" }}>
												📅 {t.createdAt ? format(parseISO(t.createdAt), "dd/MM/yyyy HH:mm") : "-"} · #{t.id}
											</Typography>
											<span
												style={{
													fontSize: "0.65rem",
													fontWeight: "bold",
													backgroundColor: st.bg,
													color: st.color,
													borderRadius: 6,
													padding: "2px 8px",
													whiteSpace: "nowrap",
												}}
											>
												{st.text}
											</span>
										</Paper>
									);
								})}
						</div>
					</Paper>

					<TicketHistoryModal
						open={!!historyTicket}
						ticket={historyTicket}
						onClose={() => setHistoryTicket(null)}
					/>

					<Paper square variant="outlined" className={classes.contactDetails}>
						<ContactModal
							open={modalOpen}
							onClose={() => setModalOpen(false)}
							contactId={contact?.id}
						></ContactModal>
						<Typography variant="subtitle1" style={{ fontWeight: "bold", marginBottom: 6 }}>
							🩺 Serviços
						</Typography>
						<div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
							{availableServices.map(serv => {
								const servName = serv.name;
								const hasServ = extraInfoState?.some(i => i.name === "Serviço de Interesse" && i.value === servName);
								return (
									<Button
										key={serv.id || servName}
										size="small"
										variant={hasServ ? "contained" : "outlined"}
										style={{
											backgroundColor: hasServ ? (serv.color || "#0284c7") : "transparent",
											color: hasServ ? "#ffffff" : (serv.color || "#0284c7"),
											borderColor: serv.color || "#0284c7",
											textTransform: "none",
											fontSize: "0.75rem",
											padding: "2px 8px"
										}}
										onClick={async () => {
											try {
												let currentInfo = Array.isArray(extraInfoState) ? [...extraInfoState] : [];
												const matchIdx = currentInfo.findIndex(i => i.name === "Serviço de Interesse" && i.value === servName);
												if (matchIdx >= 0) {
													currentInfo.splice(matchIdx, 1);
												} else {
													currentInfo.push({ name: "Serviço de Interesse", value: servName });
												}
												const { data } = await api.put(`/contacts/${contact.id}`, {
													name: contact.name,
													number: contact.number,
													email: contact.email,
													extraInfo: currentInfo
												});
												const updated = data?.extraInfo || currentInfo;
												contact.extraInfo = updated;
												setExtraInfoState([...updated]);
												toast.success(`Serviço "${servName}" atualizado!`);
											} catch (err) {
												toastError(err);
											}
										}}
									>
										{servName}
									</Button>
								);
							})}
						</div>

						{/* SEÇÃO 2: STATUS */}
						<Typography variant="subtitle1" style={{ fontWeight: "bold", marginBottom: 6 }}>
							📊 Status
						</Typography>
						<div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
							{availableStatuses.map(stat => {
								const statName = stat.name;
								const currentStatus = extraInfoState?.find(i => i.name === "Status do Cliente")?.value;
								const isSelected = currentStatus === statName;
								return (
									<Button
										key={stat.id || statName}
										size="small"
										variant={isSelected ? "contained" : "outlined"}
										style={{
											backgroundColor: isSelected ? (stat.color || "#64748b") : "transparent",
											color: isSelected ? "#ffffff" : (stat.color || "#64748b"),
											borderColor: stat.color || "#64748b",
											textTransform: "none",
											fontSize: "0.75rem",
											fontWeight: isSelected ? "bold" : "normal",
											padding: "2px 8px"
										}}
										onClick={async () => {
											try {
												let currentInfo = Array.isArray(extraInfoState) ? [...extraInfoState] : [];
												const existingIdx = currentInfo.findIndex(i => i.name === "Status do Cliente");
												if (existingIdx >= 0) {
													if (currentInfo[existingIdx].value === statName) {
														currentInfo.splice(existingIdx, 1);
													} else {
														currentInfo[existingIdx].value = statName;
													}
												} else {
													currentInfo.push({ name: "Status do Cliente", value: statName });
												}
												const { data } = await api.put(`/contacts/${contact.id}`, {
													name: contact.name,
													number: contact.number,
													email: contact.email,
													extraInfo: currentInfo
												});
												const updated = data?.extraInfo || currentInfo;
												contact.extraInfo = updated;
												setExtraInfoState([...updated]);
												toast.success(`Status alterado para "${statName}"!`);
											} catch (err) {
												toastError(err);
											}
										}}
									>
										{statName}
									</Button>
								);
							})}
						</div>

						{/* SEÇÃO 3: HISTÓRICO & ANOTAÇÕES (TIMELINE ETERNA) */}
						<Typography variant="subtitle1" style={{ fontWeight: "bold", marginBottom: 6, marginTop: 8 }}>
							📝 Histórico & Anotações (Timeline)
						</Typography>
						<Paper variant="outlined" style={{ padding: 10, marginBottom: 16, backgroundColor: "#fafafa" }}>
							<div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
								<TextField
									placeholder="Escreva um comentário ou anotação..."
									fullWidth
									size="small"
									variant="outlined"
									id="newNoteInput"
									multiline
									rows={2}
								/>
								<Button
									variant="contained"
									color="primary"
									size="small"
									style={{ textTransform: "none" }}
									onClick={async () => {
										const inputEl = document.getElementById("newNoteInput");
										const noteText = inputEl ? inputEl.value.trim() : "";
										if (!noteText) return;

										try {
											let currentInfo = Array.isArray(extraInfoState) ? [...extraInfoState] : [];
											const existingIdx = currentInfo.findIndex(i => i.name === "TimelineNotas");
											let notesList = [];
											if (existingIdx >= 0 && currentInfo[existingIdx].value) {
												try {
													notesList = JSON.parse(currentInfo[existingIdx].value);
												} catch {
													notesList = [];
												}
											}

											const newNoteObj = {
												id: Date.now(),
												text: noteText,
												createdAt: new Date().toLocaleString("pt-BR"),
												author: "Atendente"
											};

											notesList.unshift(newNoteObj);

											if (existingIdx >= 0) {
												currentInfo[existingIdx].value = JSON.stringify(notesList);
											} else {
												currentInfo.push({ name: "TimelineNotas", value: JSON.stringify(notesList) });
											}

											const { data } = await api.put(`/contacts/${contact.id}`, {
												name: contact.name,
												number: contact.number,
												email: contact.email,
												extraInfo: currentInfo
											});

											const updated = data?.extraInfo || currentInfo;
											contact.extraInfo = updated;
											setExtraInfoState([...updated]);
											inputEl.value = "";
											toast.success("Anotação adicionada ao histórico!");
										} catch (err) {
											toastError(err);
										}
									}}
								>
									Salvar
								</Button>
							</div>

							{/* LISTAGEM ROLÁVEL DA TIMELINE */}
							<div style={{ maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
								{(() => {
									const item = extraInfoState?.find(i => i.name === "TimelineNotas");
									if (!item || !item.value) return <Typography variant="caption" style={{ color: "#94a3b8" }}>Nenhuma anotação registrada ainda.</Typography>;
									try {
										const notes = JSON.parse(item.value);
										if (!Array.isArray(notes) || notes.length === 0) return <Typography variant="caption" style={{ color: "#94a3b8" }}>Nenhuma anotação registrada ainda.</Typography>;
										return notes.map(note => (
											<Paper key={note.id} variant="outlined" style={{ padding: 8, backgroundColor: "#ffffff", borderLeft: "3px solid #0284c7" }}>
												<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
													<Typography variant="caption" style={{ fontWeight: "bold", color: "#0369a1" }}>
														📅 {note.createdAt}
													</Typography>
													<IconButton
														size="small"
														style={{ padding: 2 }}
														onClick={async () => {
															try {
																let currentInfo = [...extraInfoState];
																const idx = currentInfo.findIndex(i => i.name === "TimelineNotas");
																if (idx >= 0) {
																	let currentNotes = JSON.parse(currentInfo[idx].value);
																	currentNotes = currentNotes.filter(n => n.id !== note.id);
																	currentInfo[idx].value = JSON.stringify(currentNotes);
																	const { data } = await api.put(`/contacts/${contact.id}`, {
																		name: contact.name,
																		number: contact.number,
																		email: contact.email,
																		extraInfo: currentInfo
																	});
																	const updated = data?.extraInfo || currentInfo;
																	contact.extraInfo = updated;
																	setExtraInfoState([...updated]);
																	toast.success("Anotação removida!");
																}
															} catch (err) {
																toastError(err);
															}
														}}
													>
														<DeleteOutlineIcon style={{ fontSize: 16 }} />
													</IconButton>
												</div>
												<Typography variant="body2" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
													{note.text}
												</Typography>
											</Paper>
										));
									} catch {
										return <Typography variant="caption">Formato de notas inválido.</Typography>;
									}
								})()}
							</div>
						</Paper>

						{/* SEÇÃO 4: ⏰ AGENDAR LEMBRETE */}
						<Typography variant="subtitle1" style={{ fontWeight: "bold", marginBottom: 6, marginTop: 8 }}>
							⏰ Agendar Lembrete
						</Typography>
						<Paper variant="outlined" style={{ padding: 10, marginBottom: 16, backgroundColor: "#fafafa" }}>
							<FormControl fullWidth size="small" variant="outlined" style={{ marginBottom: 8 }}>
								<InputLabel id="reminder-type-select-label">Tipo de Lembrete</InputLabel>
								<Select
									labelId="reminder-type-select-label"
									label="Tipo de Lembrete"
									value={reminderTypeId}
									onChange={(e) => setReminderTypeId(e.target.value)}
								>
									{availableReminders.map((rem) => (
										<MenuItem key={rem.id} value={rem.id}>
											<span
												style={{
													display: "inline-block",
													width: 10,
													height: 10,
													borderRadius: "50%",
													backgroundColor: rem.color || "#0284c7",
													marginRight: 6
												}}
											/>
											{rem.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							<TextField
								label="Data do Lembrete"
								type="date"
								fullWidth
								size="small"
								variant="outlined"
								InputLabelProps={{ shrink: true }}
								value={reminderDate}
								onChange={(e) => setReminderDate(e.target.value)}
								style={{ marginBottom: 8 }}
							/>

							<TextField
								placeholder="Comentário curto (ex: Consulta Dra. Stefanie)"
								fullWidth
								size="small"
								variant="outlined"
								value={reminderComment}
								onChange={(e) => setReminderComment(e.target.value)}
								style={{ marginBottom: 8 }}
							/>

							<Button
								variant="contained"
								color="primary"
								size="small"
								fullWidth
								style={{ textTransform: "none", fontWeight: "bold" }}
								onClick={async () => {
									if (!reminderTypeId) {
										toast.error("Selecione um tipo de lembrete!");
										return;
									}
									if (!reminderDate) {
										toast.error("Selecione a data do lembrete!");
										return;
									}

									const selectedRemObj = availableReminders.find(r => String(r.id) === String(reminderTypeId));

									try {
										let currentInfo = Array.isArray(extraInfoState) ? [...extraInfoState] : [];
										const existingIdx = currentInfo.findIndex(i => i.name === "LembretesCliente");
										let remList = [];
										if (existingIdx >= 0 && currentInfo[existingIdx].value) {
											try {
												remList = JSON.parse(currentInfo[existingIdx].value);
											} catch {
												remList = [];
											}
										}

										const newRemItem = {
											id: "rem_" + Date.now(),
											typeId: reminderTypeId,
											typeName: selectedRemObj?.name || "Lembrete",
											typeColor: selectedRemObj?.color || "#0284c7",
											template: selectedRemObj?.template || "",
											date: reminderDate,
											comment: reminderComment,
											status: "pending",
											createdAt: new Date().toISOString()
										};

										remList.unshift(newRemItem);

										if (existingIdx >= 0) {
											currentInfo[existingIdx].value = JSON.stringify(remList);
										} else {
											currentInfo.push({ name: "LembretesCliente", value: JSON.stringify(remList) });
										}

										const { data } = await api.put(`/contacts/${contact.id}`, {
											name: contact.name,
											number: contact.number,
											email: contact.email,
											extraInfo: currentInfo
										});

										const updated = data?.extraInfo || currentInfo;
										contact.extraInfo = updated;
										setExtraInfoState([...updated]);

										setReminderTypeId("");
										setReminderDate("");
										setReminderComment("");
										toast.success("Lembrete agendado com sucesso!");
									} catch (err) {
										toastError(err);
									}
								}}
							>
								📅 Agendar Lembrete
							</Button>

							{/* LISTAGEM DOS LEMBRETES DO CLIENTE */}
							<div style={{ marginTop: 12, maxHeight: 200, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
								{(() => {
									const item = extraInfoState?.find(i => i.name === "LembretesCliente");
									if (!item || !item.value) return <Typography variant="caption" style={{ color: "#94a3b8" }}>Nenhum lembrete agendado.</Typography>;
									try {
										const remindersList = JSON.parse(item.value);
										if (!Array.isArray(remindersList) || remindersList.length === 0) return <Typography variant="caption" style={{ color: "#94a3b8" }}>Nenhum lembrete agendado.</Typography>;
										return remindersList.map(rem => {
											const dNow = new Date();
											const todayStr = `${dNow.getFullYear()}-${String(dNow.getMonth() + 1).padStart(2, "0")}-${String(dNow.getDate()).padStart(2, "0")}`;
											const remDateStr = rem.date ? rem.date.split("T")[0] : "";
											let statusBg = "#fffbeb"; // Amarelo (A Vencer)
											let statusBorder = "#f59e0b";
											let statusText = "🟡 A Vencer";
											let statusBadgeBg = "#fef3c7";
											let statusBadgeColor = "#b45309";

											if (rem.status === "completed") {
												statusBg = "#f0fdf4"; // Verde (Feito)
												statusBorder = "#16a34a";
												statusText = "✅ Feito";
												statusBadgeBg = "#dcfce7";
												statusBadgeColor = "#15803d";
											} else if (remDateStr < todayStr) {
												statusBg = "#fef2f2"; // Vermelho (Atrasado)
												statusBorder = "#dc2626";
												statusText = "🔴 Atrasado";
												statusBadgeBg = "#fee2e2";
												statusBadgeColor = "#b91c1c";
											}

											return (
												<Paper key={rem.id} variant="outlined" style={{ padding: 8, backgroundColor: statusBg, borderLeft: `5px solid ${statusBorder}` }}>
													<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
														<div>
															<span style={{ fontSize: "0.68rem", fontWeight: "bold", backgroundColor: statusBadgeBg, color: statusBadgeColor, padding: "2px 6px", borderRadius: 4, marginRight: 6 }}>
																{statusText}
															</span>
															<span style={{ fontSize: "0.75rem", fontWeight: "bold", color: "#1e293b" }}>
																⏰ {rem.typeName}
															</span>
															<span style={{ fontSize: "0.7rem", color: "#64748b", marginLeft: 6 }}>
																({rem.date?.includes("-") ? rem.date.split("-").reverse().join("/") : rem.date})
															</span>
														</div>
														<div>
															<IconButton
																size="small"
																style={{ padding: 2, color: rem.status === "completed" ? "#16a34a" : "#94a3b8" }}
																title={rem.status === "completed" ? "Marcar como Pendente" : "Marcar como Feito"}
																onClick={async () => {
																	try {
																		let currentInfo = [...extraInfoState];
																		const idx = currentInfo.findIndex(i => i.name === "LembretesCliente");
																		if (idx >= 0) {
																			let list = JSON.parse(currentInfo[idx].value);
																			list = list.map(r => r.id === rem.id ? { ...r, status: r.status === "completed" ? "pending" : "completed" } : r);
																			currentInfo[idx].value = JSON.stringify(list);
																			const { data } = await api.put(`/contacts/${contact.id}`, {
																				name: contact.name,
																				number: contact.number,
																				email: contact.email,
																				extraInfo: currentInfo
																			});
																			const updated = data?.extraInfo || currentInfo;
																			contact.extraInfo = updated;
																			setExtraInfoState([...updated]);
																			toast.success("Status do lembrete atualizado!");
																		}
																	} catch (err) {
																		toastError(err);
																	}
																}}
															>
																<CheckIcon style={{ fontSize: 16 }} />
															</IconButton>
															<IconButton
																size="small"
																style={{ padding: 2 }}
																onClick={async () => {
																	try {
																		let currentInfo = [...extraInfoState];
																		const idx = currentInfo.findIndex(i => i.name === "LembretesCliente");
																		if (idx >= 0) {
																			let list = JSON.parse(currentInfo[idx].value);
																			list = list.filter(r => r.id !== rem.id);
																			currentInfo[idx].value = JSON.stringify(list);
																			const { data } = await api.put(`/contacts/${contact.id}`, {
																				name: contact.name,
																				number: contact.number,
																				email: contact.email,
																				extraInfo: currentInfo
																			});
																			const updated = data?.extraInfo || currentInfo;
																			contact.extraInfo = updated;
																			setExtraInfoState([...updated]);
																			toast.success("Lembrete removido!");
																		}
																	} catch (err) {
																		toastError(err);
																	}
																}}
															>
																<DeleteOutlineIcon style={{ fontSize: 16 }} />
															</IconButton>
														</div>
													</div>
													{rem.comment && (
														<Typography variant="body2" style={{ fontSize: "0.75rem", color: "#334155", marginTop: 4 }}>
															📝 {rem.comment}
														</Typography>
													)}
												</Paper>
											);
										});
									} catch {
										return <Typography variant="caption">Formato de lembretes inválido.</Typography>;
									}
								})()}
							</div>
						</Paper>

						<Typography variant="subtitle1">
							{i18n.t("contactDrawer.extraInfo")}
						</Typography>
						{extraInfoState?.filter(info => info.name !== "TimelineNotas" && info.name !== "Serviço de Interesse" && info.name !== "Status do Cliente" && info.name !== "LembretesCliente")?.map(info => (
							<Paper
								key={info.id}
								square
								variant="outlined"
								className={classes.contactExtraInfo}
							>
								<InputLabel>{info.name}</InputLabel>
								<Typography component="div" noWrap style={{ paddingTop: 2 }}>
									<MarkdownWrapper>{info.value}</MarkdownWrapper>
								</Typography>
							</Paper>
						))}
					</Paper>
				</div>
			)}
		</Drawer>
	);
};

export default ContactDrawer;
